<template>
  <div>
    <v2-button
      variant="outline"
      @click="showModal = true"
    >
      Upload Invoice
    </v2-button>

    <v2-modal
      :show="showModal"
      title="Upload Invoice"
      @close="closeModal"
    >
      <div
        @dragover.prevent
        @drop.prevent
      >
        <div v-if="!orderInvoiceFile">
          <label class="v2-upload-invoice__label">
            Upload Invoice
            <span class="v2-upload-invoice__required">*</span>
          </label>
          <div
            class="v2-upload-invoice__dropzone"
            @drop="onDropOrderInvoiceFile"
            @click="triggerFileInput"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line
                x1="12"
                y1="3"
                x2="12"
                y2="15"
              />
            </svg>
            <p class="v2-upload-invoice__dropzone-title">
              Drag & Drop
            </p>
            <p class="v2-upload-invoice__dropzone-subtitle">
              or click to select files from your system
            </p>
          </div>
          <input
            ref="fileInput"
            type="file"
            class="v2-upload-invoice__hidden-input"
            @change="onFileSelected"
          >
        </div>

        <div
          v-else
          class="v2-upload-invoice__file-info"
          @drop="onDropOrderInvoiceFile"
        >
          <div class="v2-upload-invoice__file-details">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <div>
              <div class="v2-upload-invoice__file-name">
                {{ orderInvoiceFile.name }}
              </div>
              <div class="v2-upload-invoice__file-size">
                {{ formatFileSize(orderInvoiceFile.size) }}
              </div>
            </div>
          </div>
          <button
            class="v2-upload-invoice__remove-btn"
            @click="orderInvoiceFile = null"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        </div>
      </div>

      <template #footer>
        <v2-button
          variant="outline"
          @click="closeModal"
        >
          Cancel
        </v2-button>
        <v2-button
          variant="primary"
          :disabled="!orderInvoiceFile || isUploading"
          @click="updateOrderInvoiceFile"
        >
          {{ isUploading ? 'Uploading...' : 'Upload' }}
        </v2-button>
      </template>
    </v2-modal>
  </div>
</template>

<script>
import V2Button from '@/components/v2/V2Button.vue'
import V2Modal from '@/components/v2/V2Modal.vue'
import store from '@/store'
import { apiToastError, apiToastSuccess } from '@/@core/utils/toast'
import { FILE_TYPES } from '@/constants'
import { UPLOAD_DOCUMENTS } from '@/store/modules/common.module'
import { UPDATE_ORDER } from '@/store/modules/order.module'

export default {
  name: 'V2OrderUploadInvoice',
  components: {
    V2Button,
    V2Modal,
  },
  props: {
    order: {
      type: Object,
      default: () => ({}),
    },
    loadOrder: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      showModal: false,
      orderInvoiceFile: null,
      orderInvoiceFileLocation: null,
      isUploading: false,
    }
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    onFileSelected(e) {
      const file = e.target.files[0]
      if (file) {
        this.orderInvoiceFile = file
      }
    },
    onDropOrderInvoiceFile(e) {
      const droppedFile = e.dataTransfer.files[0]
      if (droppedFile) {
        this.orderInvoiceFile = droppedFile
      }
    },
    formatFileSize(bytes) {
      return `${(bytes / 1024).toFixed(2)} KB`
    },
    async updateOrderInvoiceFile() {
      this.isUploading = true
      try {
        if (this.orderInvoiceFile instanceof File) {
          this.orderInvoiceFileLocation = await store.dispatch(UPLOAD_DOCUMENTS, {
            fileType: FILE_TYPES.ORDER_INVOICE,
            file: this.orderInvoiceFile,
          })
        }
        const payload = {
          invoiceUrl: this.orderInvoiceFileLocation,
        }
        await store.dispatch(UPDATE_ORDER, {
          orderId: this.order._id,
          payload,
        })
        apiToastSuccess('Your Invoice document has been uploaded successfully')
        this.clearForm()
        this.showModal = false
        this.loadOrder(this.order._id)
      } catch (err) {
        apiToastError(err)
      } finally {
        this.isUploading = false
      }
    },
    clearForm() {
      this.orderInvoiceFile = null
      this.orderInvoiceFileLocation = null
    },
    closeModal() {
      this.clearForm()
      this.showModal = false
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-upload-invoice {
  &__label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
    margin-bottom: 0.5rem;
  }

  &__required {
    color: var(--v2-destructive);
  }

  &__dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    border: 2px dashed var(--v2-border);
    border-radius: 0.625rem;
    cursor: pointer;
    color: var(--v2-muted-foreground);
    transition: border-color 0.15s ease;

    &:hover {
      border-color: var(--v2-primary);
    }
  }

  &__dropzone-title {
    font-size: 0.9375rem;
    font-weight: 700;
    color: var(--v2-foreground);
    margin: 0.5rem 0 0.25rem;
  }

  &__dropzone-subtitle {
    font-size: 0.8125rem;
    color: var(--v2-muted-foreground);
    margin: 0;
  }

  &__hidden-input {
    display: none;
  }

  &__file-info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border: 1px solid var(--v2-border);
    border-radius: 0.625rem;
    background: var(--v2-muted);
  }

  &__file-details {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: var(--v2-muted-foreground);
  }

  &__file-name {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
  }

  &__file-size {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
  }

  &__remove-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.25rem;
    background: none;
    border: none;
    color: var(--v2-destructive);
    cursor: pointer;
    border-radius: 0.25rem;

    &:hover {
      background: rgba(229, 72, 77, 0.1);
    }
  }
}
</style>
